/**
 * @module Worker
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
/* tslint:disable:use-webworker-statestore */ // need to allow UI dev teams to use angular http as a backup to worker

import { Injectable } from '@angular/core';
import { WorkerInput, OptionalInputArgs } from '@core/worker/core/worker-input';
import { WorkerOutput } from '@core/worker/core/worker-output';
import { Observable, Subscriber } from 'rxjs';
import { RequestParams } from '@core/worker/http/models/request-params';
import { WorkerIpcService } from '@core/worker/services/worker-ipc.service';
import { WorkerUtils } from '@core/worker/models/utils';
import { WorkerRequestArgs } from '@core/worker/models/worker-request-args';
import { WorkerRequest } from '@core/worker/models/worker-request';
import { AppConfig } from './../../../root/app.config';
import { SETTINGS } from '@environment/settings';
import { Md5 } from 'ts-md5/dist/md5';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { map } from 'rxjs/operators';

@Injectable()
export class WorkerService {
  /**
   * Determines if we should use the worker or http module
   * @TODO remove this once Webworker is fully validated
   */
  private activeWorker: boolean;

  constructor(
    private workerIpc: WorkerIpcService,
    private appConfig: AppConfig,
    private httpClientService?: HttpClient
  ) {
    this.activeWorker = this.appConfig.getConfig('webworker') || SETTINGS.WEB_WORKER;
  }

  /**
   * Performs any type of http request to the webworker. First argument is required, and can either be a url or
   * a {@link Request} instance. If the first argument is a url, an optional {@link RequestOptions}
   * object can be provided as the 2nd argument. The options object will be merged with the values
   * of {@link BaseRequestOptions} before performing the request.
   *
   * @TODO merge RequestOptionsArgs with OptionalInputArgs
   */
  public request(
    url: string,
    method: string,
    options: WorkerRequestArgs = {}
  ): Observable<HttpResponse<Object>> {
    const workerRequest = new WorkerRequest(url, method, options);
    workerRequest.body = this.setBody(workerRequest);
    const reqParams: RequestParams = new RequestParams(
      workerRequest.url,
      workerRequest.method,
      workerRequest.body
    );
    // workerRequest.queryParams = {type: 'activity'};

    if (workerRequest.queryParams) {
      reqParams.queryParams = workerRequest.queryParams;
    }
    if (workerRequest.cacheKey) {
      workerRequest.cacheKey = Md5.hashStr(workerRequest.cacheKey).toString();
    }
    // @TODO Fix
    const opts: OptionalInputArgs = {
      cacheFallback: workerRequest.cacheFallback,
      cacheResponse: workerRequest.cacheResponse,
      preferCache: workerRequest.preferCache,
      cacheKey: workerRequest.cacheKey,
      retry: workerRequest.retry,
      fallback: workerRequest.fallback,
      preflight: workerRequest.preflight,
      validateRequest: workerRequest.validateRequest,
      validateResponse: workerRequest.validateResponse,
      swaggerUrl: workerRequest.swaggerUrl,
      queue: workerRequest.queue,
      ttl: workerRequest.ttl,
    };

    if (this.activeWorker) {
      reqParams.headers = workerRequest.headers ? workerRequest.headers : reqParams.headers;
      const input: WorkerInput = new WorkerInput(reqParams, opts);
      return this.workerIpc.toWorker(input).pipe(
        map((res: WorkerOutput) => {
          return WorkerUtils.buildResponse(res);
        })
      );
    } else {
      return this.httpRequest(method, url, workerRequest.body, options);
    }
  }
  /**
   * Performs a request with `get` http method.
   * @TODO worker options
   */
  public get(url: string, options: WorkerRequestArgs = {}): Observable<HttpResponse<Object>> {
    /* @TODO need to remove below hotfix once US316318 is completed  */
    return WorkerUtils.isIE()
      ? this.httpGetRequest(url, options)
      : this.request(url, 'GET', options);
  }

  /**
   * Performs a request with `post` http method.
   * @TODO worker options
   */
  public post(
    url: string,
    body: any,
    options: WorkerRequestArgs = {}
  ): Observable<HttpResponse<Object>> {
    options.body = body;
    /* @TODO need to remove below hotfix once US316318 is completed  */
    return WorkerUtils.isIE()
      ? this.httpRequest('POST', url, body, options)
      : this.request(url, 'POST', options);
  }

  /**
   * Performs an Epay request with `post` http method.
   * @TODO worker options
   */
  public postEpayRequest(
    url: string,
    body: any,
    options: WorkerRequestArgs = {}
  ): Observable<HttpResponse<Object>> {
    options.body = body;
    return this.httpRequest('POST', url, body, options);
  }
  /**
   * Performs a request with `put` http method.
   * @TODO worker options
   */
  public put(
    url: string,
    body: any,
    options: WorkerRequestArgs = {}
  ): Observable<HttpResponse<Object>> {
    options.body = body;
    return WorkerUtils.isIE()
      ? this.httpRequest('PUT', url, body, options)
      : this.request(url, 'PUT', options);
  }

  /**
   * Performs a request with `delete` http method.
   * @TODO worker options
   */
  public delete(
    url: string,
    body: any,
    options: WorkerRequestArgs = {}
  ): Observable<HttpResponse<Object>> {
    options.body = body;
    return WorkerUtils.isIE()
      ? this.httpRequest('DELETE', url, body, options)
      : this.request(url, 'DELETE', options);
  }

  /**
   * Performs a request with `patch` http method.
   * @TODO worker options
   */
  public patch(
    url: string,
    body: any,
    options: WorkerRequestArgs = {}
  ): Observable<HttpResponse<Object>> {
    options.body = body;
    return this.request(url, 'PATCH', options);
  }

  /**
   * Performs a request with `head` http method.
   * @TODO worker options
   */
  public head(url: string, options: WorkerRequestArgs = {}): Observable<HttpResponse<Object>> {
    return this.request(url, 'HEAD', options);
  }

  /**
   * Performs a request with `options` http method.
   * @TODO worker options
   */
  public options(url: string, options: WorkerRequestArgs = {}): Observable<HttpResponse<Object>> {
    return this.request(url, 'OPTIONS', options);
  }

  private setBody(workerRequest: WorkerRequest): string {
    return workerRequest.body && typeof workerRequest.body === 'object'
      ? JSON.stringify(workerRequest.body)
      : workerRequest.body;
  }
  /**
   * Performs a request with http post
   * @TODO need to remove below hotfix once US316318 is completed
   */

  private httpMethod(method: string, url: string, body: any, options: WorkerRequestArgs = {}): any {
    options.body = body;
    const keys = options.headers ? options.headers.keys() : [];
    const headerObj: ObjectInterface = {};
    keys.forEach((key: string) => {
      headerObj[key] = options.headers.get(key);
    });
    const httpHeaders: HttpHeaders = new HttpHeaders(headerObj);
    if (method === 'POST') {
      return this.httpClientService.post(url, body, {
        headers: httpHeaders,
        observe: 'response',
        params: options.params || null,
      });
    } else if (method === 'PUT') {
      return this.httpClientService.put(url, body, {
        headers: httpHeaders,
        observe: 'response',
        params: options.params || null,
      });
    } else if (method === 'DELETE') {
      return this.httpClientService.request(method, url, {
        headers: httpHeaders,
        observe: 'response',
        params: options.params || null,
        body: options.body || null,
      });
    }
  }

  /**
   * Performs a request with http post
   * @TODO need to remove below hotfix once US316318 is completed
   */

  private httpGet(url: string, options: WorkerRequestArgs = {}): any {
    const keys = options.headers ? options.headers.keys() : [];
    const headerObj: ObjectInterface = {};
    keys.forEach((key: string) => {
      headerObj[key] = options.headers.get(key);
    });
    const httpHeaders: HttpHeaders = new HttpHeaders(headerObj);
    return this.httpClientService.get(url, {
      headers: httpHeaders,
      params: options.params,
      observe: 'response',
    });
  }

  /**
   * Create observable and json method to support http response
   * @TODO need to remove below hotfix once US316318 is completed
   */
  private httpRequest(
    method: string,
    url: string,
    body: any,
    options: WorkerRequestArgs
  ): Observable<HttpResponse<Object>> {
    return new Observable((observer: Subscriber<HttpResponse<Object>>) => {
      this.httpMethod(method, url, body, options).subscribe(
        (data: any) => {
          // tslint:disable-next-line:no-string-literal
          data.json = () => {
            return data['body'];
          };
          observer.next(data);
          observer.complete();
          return observer;
        },
        (error: any) => {
          error.json = () => {
            return error.error;
          };
          error.body = error.error;
          observer.next(error);
          observer.complete();
          return observer;
        }
      );
    });
  }

  /**
   * Create observable and json method to support http response
   * @TODO need to remove below hotfix once US316318 is completed
   */
  private httpGetRequest(
    url: string,
    options: WorkerRequestArgs
  ): Observable<HttpResponse<Object>> {
    return new Observable((observer: Subscriber<HttpResponse<Object>>) => {
      this.httpGet(url, options).subscribe(
        (data: any) => {
          // tslint:disable-next-line:no-string-literal
          data.json = () => {
            return data['body'];
          };
          observer.next(data);
          observer.complete();
          return observer;
        },
        (error: any) => {
          error.json = () => {
            return error.error;
          };
          error.body = error.error;
          observer.next(error);
          observer.complete();
          return observer;
        }
      );
    });
  }
}
